import type { UIPosition } from '@hotjar/webapp/domains/highlights/api';
import { Button, Modal } from '@hotjar/hotsauce';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHighlightRemove } from './use-highlight-remove';

export interface DeleteModal {
  siteId: string;
  highlightId: string;
  onModalClose: (isDeleteAborted: boolean) => void;
  className?: string;
  isOpen?: boolean;
  uiPosition?: UIPosition;
}

export function DeleteModal({
  siteId,
  highlightId,
  onModalClose,
  className,
  isOpen = false,
  uiPosition,
}: DeleteModal): JSX.Element {
  const { t } = useTranslation();
  // TODO: handle loading and error state
  const { mutateAsync: removeHighlight } = useHighlightRemove(siteId, uiPosition);

  const abortRemoval = useCallback(() => {
    onModalClose(true);
  }, [onModalClose]);

  const confirmRemoval = useCallback(async () => {
    await removeHighlight({ highlightId });
    onModalClose(false);
  }, [removeHighlight, onModalClose, highlightId]);

  return (
    <Modal
      contentLabel={t('highlights:single.remove.confirmation-modal-label')}
      hideCloseButton={false}
      isOpen={isOpen}
      onClose={abortRemoval}
      className={className}
    >
      <Modal.Header title={t('highlights:single.remove.header')} />
      <Modal.Body>{t('highlights:single.remove.body')}</Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={abortRemoval}>
          {t('highlights:single.remove.abort')}
        </Button>
        <Button kind="danger" onClick={confirmRemoval} type="submit" data-testid="confirm">
          {t('highlights:single.remove.confirm')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
